/**
 * The assistant panel (#765, spec §8.1–8.2): quota, the conversation (a
 * `MessageScroller` of turns), the composer with suggestion chips. Rendered
 * inside `AssistantDrawer` and on the `/assistant` page (#765 Ф3) — the same
 * panel, not a second UI; the state comes from `useAssistant` through
 * `AssistantSession`, which the drawer keeps mounted outside the sheet so a
 * question in flight survives a close (Codex P2, round 23); the player
 * bridge from `AssistantHostContext`.
 *
 * Reads no flags and no auth context itself — `apiClient`/`selectedOrgId`
 * reach the session as props (same rule as `RecordDetailInline`), so the
 * same panel serves the standalone app and the Bitrix24 iframe.
 */

import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Sparkles } from 'lucide-react';
import {
  ASSISTANT_QUESTION_MAX_CHARS,
  type AssistantCitation,
  type AssistantScope,
} from '@aica/api-types/schemas';
import type { AssistantTurnView } from '@/hooks/assistant/useAssistant';
import {
  useAssistantHost,
  useAssistantRecordOpener,
} from '@/contexts/AssistantHostContext';
import { useAssistantDrawerSafe } from '@/contexts/AssistantDrawerContext';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonCard } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/modals/ConfirmDialog';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from '@/components/ui/chat/message-scroller';
import { Message, MessageContent } from '@/components/ui/chat/message';
import { Bubble, BubbleContent } from '@/components/ui/chat/bubble';
import { Marker, MarkerContent, MarkerIcon } from '@/components/ui/chat/marker';
import { AssistantAnswer } from './AssistantAnswer';
import { AssistantTurnFilters } from './AssistantTurnFilters';
import { LongWaitHint } from './LongWaitHint';
import { useAssistantSession } from './AssistantSession';
import { cn } from '@/lib/utils';
import { formatResetTime } from '@/lib/assistant/reset-time';
import { assistantErrorKeys } from '@/lib/assistant/error-keys';

/** Follow-up prompts offered before the first question (§8.2), i18n keys. */
const SUGGESTION_KEYS = [
  'summary',
  'agreements',
  'objections',
  'nextStep',
  'risks',
  'followUp',
] as const;

/** The same, for a deal's whole feed (#765 Ф2) — questions across conversations. */
const ENTITY_SUGGESTION_KEYS = [
  'summary',
  'agreements',
  'objections',
  'nextStep',
  'risks',
  'changes',
] as const;

/** The same, for the whole base (#765 Ф3) — questions a manager asks of all calls. */
const ORG_SUGGESTION_KEYS = [
  'objections',
  'complaints',
  'lostDeals',
  'competitors',
  'bestPractices',
  'prices',
] as const;

/** Per contour: the suggestions, and the i18n keys that differ by contour. */
function contourCopy(scope: AssistantScope) {
  switch (scope) {
    case 'entity':
      return {
        suggestions: ENTITY_SUGGESTION_KEYS.map((key) => `suggestionsEntity.${key}`),
        emptyTitle: 'empty.titleEntity',
        emptyDescription: 'empty.descriptionEntity',
        placeholder: 'composer.placeholderEntity',
      };
    case 'org':
      return {
        suggestions: ORG_SUGGESTION_KEYS.map((key) => `suggestionsOrg.${key}`),
        emptyTitle: 'empty.titleOrg',
        emptyDescription: 'empty.descriptionOrg',
        placeholder: 'composer.placeholderOrg',
      };
    default:
      return {
        suggestions: SUGGESTION_KEYS.map((key) => `suggestions.${key}`),
        emptyTitle: 'empty.title',
        emptyDescription: 'empty.description',
        placeholder: 'composer.placeholder',
      };
  }
}

/** Seconds of audio a quote plays (§8.2). */
const QUOTE_CLIP_SECONDS = 20;
/** The counter appears once the draft is this close to the cap. */
const LIMIT_COUNTER_FROM = Math.floor(ASSISTANT_QUESTION_MAX_CHARS * 0.8);

export interface AssistantPanelProps {
  /** A turn settled — the embedded host re-fits its iframe (§8.0). */
  onLayoutChange?: () => void;
}

function isInFlight(turn: AssistantTurnView): boolean {
  return turn.status === 'pending' || turn.status === 'running';
}

export function AssistantPanel({ onLayoutChange }: AssistantPanelProps) {
  const { t } = useTranslation('assistant');
  const host = useAssistantHost();
  // The surface's own place for records (the CRM tab) — also when no record is open in it.
  const surfaceOpenRecord = useAssistantRecordOpener();
  const { appNavigate } = useAppNavigate();
  const assistant = useAssistantSession();
  // The session's subject, not the drawer's topic: on the `/assistant` page
  // the drawer may be about a record while the page asks the whole base.
  const scope = assistant.subject.scope;
  const copy = contourCopy(scope);
  const suggestions = copy.suggestions.map((key) => t(key));
  // Kept by the session, per topic: the panel is remounted whenever the topic
  // changes, and an unsent question must not die because the user glanced at
  // the next record (spec §10.16, #922).
  const { draft, setDraft } = assistant;
  const [repeatCandidate, setRepeatCandidate] = useState<string | null>(null);
  // Verdicts already on screen when history first lands are history, not
  // news: announced (`role="alert"`) is only a verdict that arrives while
  // the panel is open — including one for a turn still in flight at that
  // moment (owner's review, 17.09).
  const [historical, setHistorical] = useState<ReadonlySet<string> | null>(null);

  const asking = assistant.state === 'asking';
  // A record or a deal the subject's access no longer lets it ask about — a
  // panel held open over a turn in flight when the grant answered (#1677,
  // review #1709): the turn stays readable, no new question is taken.
  const recordRefused = useAssistantDrawerSafe()?.recordRefused ?? false;
  const accessRefused = scope !== 'org' && recordRefused;
  // A spent day and a contour stopped by the kill switch close the composer
  // the same way; the history above it stays readable (#1059).
  const composerClosed =
    assistant.quota?.remainingToday === 0 || assistant.paused || accessRefused;
  const atLimit = draft.length >= ASSISTANT_QUESTION_MAX_CHARS;

  useEffect(() => {
    if (historical !== null || assistant.isLoadingHistory) return;
    setHistorical(
      new Set(
        assistant.turns.filter((turn) => !isInFlight(turn)).map((turn) => turn.turnId)
      )
    );
  }, [historical, assistant.isLoadingHistory, assistant.turns]);

  useEffect(() => {
    onLayoutChange?.();
  }, [assistant.state, assistant.turns.length, onLayoutChange]);

  const submit = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      // One gate for every way in — the composer, the prompts, a follow-up
      // under an answer (Codex P2, round 8).
      // …and not before history has been read: the repeat check needs the
      // thread it would repeat in (Codex P2, round 11).
      if (
        !trimmed ||
        asking ||
        composerClosed ||
        assistant.historyError ||
        assistant.isLoadingHistory
      )
        return;
      // The hook knows the thread's questions — on screen, typed here, and
      // remembered beyond the visible window (Codex P2, rounds 10 and 19).
      const repeated = assistant.isRepeat(trimmed);
      if (repeated) {
        setRepeatCandidate(trimmed);
        return;
      }
      setDraft('');
      void assistant.ask(trimmed);
    },
    [asking, assistant, composerClosed, setDraft]
  );

  const confirmRepeat = useCallback(() => {
    const question = repeatCandidate;
    setRepeatCandidate(null);
    if (!question) return;
    setDraft('');
    void assistant.ask(question);
  }, [assistant, repeatCandidate, setDraft]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter sends, Shift+Enter breaks the line. An IME composition's Enter
      // (keyCode 229) confirms the candidate and must not send.
      if (event.key !== 'Enter' || event.shiftKey) return;
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      submit(draft);
    },
    [draft, submit]
  );

  const handleQuote = useCallback(
    (citation: AssistantCitation) => {
      if (citation.startSec === null) {
        // No timecode: only a quote from another conversation is clickable,
        // and it opens that record from its start (#1041 review).
        if (host?.recordId === citation.recordId) return;
        const open = host?.openRecord ?? surfaceOpenRecord;
        if (open) {
          open(citation.recordId);
          return;
        }
        appNavigate(`/records?id=${citation.recordId}`, {
          embeddedTo: {
            to: '/embedded/dashboard/',
            search: { tab: 'records', recordId: citation.recordId },
          },
        });
        return;
      }
      if (host && host.recordId === citation.recordId) {
        host.requestPlay(citation.startSec, QUOTE_CLIP_SECONDS);
        return;
      }
      // The record is not open (closed, or another one): the host that has a
      // place for records opens it there; otherwise a deep link (§8.3).
      // `t` is an integer — both routes validate it with Number.isSafeInteger.
      const at = Math.floor(citation.startSec);
      const open = host?.openRecord ?? surfaceOpenRecord;
      if (open) {
        open(citation.recordId, at);
        return;
      }
      appNavigate(`/records?id=${citation.recordId}&t=${at}&clip=${QUOTE_CLIP_SECONDS}`, {
        embeddedTo: {
          to: '/embedded/dashboard/',
          search: {
            tab: 'records',
            recordId: citation.recordId,
            t: at,
            clip: QUOTE_CLIP_SECONDS,
          },
        },
      });
    },
    [appNavigate, host, surfaceOpenRecord]
  );

  // Held back only while the cited record itself is open with its audio still
  // decoding; a citation of another record deep-links regardless of what the
  // open record can play (Codex P2, round 3).
  const quotaLabel = useMemo(() => {
    if (assistant.paused) return t('errors.assistant_paused');
    const quota = assistant.quota;
    if (!quota) return null;
    if (quota.remainingToday === null) return t('quota.unlimited');
    if (quota.remainingToday === 0) {
      return t('quota.exhausted', { time: formatResetTime(quota.resetAt) });
    }
    return t('quota.remaining', { count: quota.remainingToday });
  }, [assistant.paused, assistant.quota, t]);

  if (assistant.blockedCode) {
    const pro = assistant.blockedCode === 'tier_not_eligible';
    return (
      <div className="flex-1 min-h-0 overflow-y-auto">
        <EmptyState
          icon={Sparkles}
          size="sm"
          title={
            pro
              ? t('empty.proTitle')
              : t(assistantErrorKeys(assistant.blockedCode, scope))
          }
          description={pro ? t('empty.proDescription') : undefined}
        />
      </div>
    );
  }

  return (
    <>
      {/* Quota and a new thread — the scope switcher lives in the drawer,
          outside this keyed panel (#1041 review). */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 sm:px-5 md:px-6">
        <span className="flex-1" />
        {quotaLabel && (
          <span className="text-xs text-fg-muted" data-testid="assistant-quota">
            {quotaLabel}
          </span>
        )}
        {assistant.turns.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            // Not while a question is in flight: the hook refuses it too (Codex P1, round 6).
            disabled={asking}
            onClick={assistant.startNewThread}
          >
            {t('composer.newThread')}
          </Button>
        )}
      </div>

      {/* Conversation */}
      <div className="flex-1 min-h-0 px-4 py-4 sm:px-5 md:px-6">
        <MessageScrollerProvider>
          <MessageScroller>
            <MessageScrollerViewport aria-label={t('a11y.conversation')}>
              <MessageScrollerContent>
                {assistant.historyError && (
                  <EmptyState
                    type="error"
                    size="sm"
                    title={t('history.loadFailed')}
                    primaryAction={{
                      label: t('history.retry'),
                      onClick: assistant.reloadHistory,
                    }}
                    data-testid="assistant-history-error"
                  />
                )}
                {!assistant.historyError &&
                  assistant.turns.length === 0 &&
                  !assistant.isLoadingHistory && (
                    <div className="flex flex-col gap-4">
                      <EmptyState
                        icon={Sparkles}
                        size="sm"
                        title={t(copy.emptyTitle)}
                        description={t(copy.emptyDescription)}
                      />
                      <div className="flex flex-wrap justify-center gap-2">
                        {suggestions.map((suggestion) => (
                          <Button
                            key={suggestion}
                            variant="outline"
                            size="sm"
                            disabled={asking || composerClosed}
                            onClick={() => submit(suggestion)}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                {assistant.turns.map((turn) => (
                  <div key={turn.turnId} className="flex flex-col gap-3">
                    <MessageScrollerItem
                      messageId={`${turn.turnId}:question`}
                      scrollAnchor
                    >
                      <Message align="end">
                        <MessageContent>
                          <Bubble align="end" variant="subtle">
                            <BubbleContent className="whitespace-pre-line">
                              {turn.question}
                            </BubbleContent>
                          </Bubble>
                        </MessageContent>
                      </Message>
                    </MessageScrollerItem>
                    <MessageScrollerItem messageId={turn.turnId}>
                      <Message>
                        <MessageContent>
                          {turn.filters && (
                            <AssistantTurnFilters filters={turn.filters} />
                          )}
                          {turn.answer ? (
                            <Bubble variant="ghost">
                              <BubbleContent>
                                <AssistantAnswer
                                  answer={turn.answer}
                                  onQuote={handleQuote}
                                  onSuggested={submit}
                                  suggestionsDisabled={asking || composerClosed}
                                  scope={scope}
                                  onScreenRecordId={host?.recordId ?? null}
                                  live={
                                    historical !== null && !historical.has(turn.turnId)
                                  }
                                  filtered={Boolean(turn.filters)}
                                />
                              </BubbleContent>
                            </Bubble>
                          ) : isInFlight(turn) ? (
                            <div className="flex flex-col gap-2">
                              <Marker role="status" aria-live="polite">
                                <MarkerIcon>
                                  <Sparkles className="animate-pulse" />
                                </MarkerIcon>
                                <MarkerContent>
                                  {turn.turnId === assistant.activeTurnId || asking
                                    ? t('status.thinking')
                                    : t('status.inProgress')}
                                </MarkerContent>
                              </Marker>
                              {scope === 'org' && <LongWaitHint since={turn.createdAt} />}
                              <SkeletonCard showAvatar={false} lines={3} />
                            </div>
                          ) : (
                            <Marker role="status">
                              <MarkerContent>
                                {turn.status === 'failed'
                                  ? t('status.failed')
                                  : t('status.timeout')}
                              </MarkerContent>
                            </Marker>
                          )}
                        </MessageContent>
                      </Message>
                    </MessageScrollerItem>
                  </div>
                ))}

                {assistant.state === 'timeout' && (
                  <Marker role="status" aria-live="polite">
                    <MarkerContent>{t('status.timeout')}</MarkerContent>
                  </Marker>
                )}
              </MessageScrollerContent>
            </MessageScrollerViewport>
            <MessageScrollerButton />
          </MessageScroller>
        </MessageScrollerProvider>
      </div>

      {/* Composer — pb-chat-safe keeps it clear of the support widget corner (§8.0). */}
      <form
        className="border-t border-border px-4 pt-3 pb-chat-safe sm:px-5 md:px-6"
        onSubmit={(event) => {
          event.preventDefault();
          submit(draft);
        }}
      >
        <div className="flex items-end gap-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            autoResize
            minRows={1}
            maxRows={6}
            placeholder={t(copy.placeholder)}
            aria-label={t(copy.placeholder)}
            maxLength={ASSISTANT_QUESTION_MAX_CHARS}
            disabled={composerClosed || assistant.historyError}
            className={cn('flex-1', composerClosed && 'opacity-60')}
          />
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={
              asking ||
              composerClosed ||
              assistant.historyError ||
              assistant.isLoadingHistory ||
              draft.trim() === ''
            }
            isLoading={asking}
            aria-label={t('composer.send')}
          >
            <Send className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <p className="mt-1 flex items-center justify-between gap-2 text-xs text-fg-subtle">
          <span data-testid="assistant-composer-hint">
            {accessRefused
              ? t('scope.recordRefused')
              : atLimit
                ? t('composer.atLimit')
                : t('composer.hint')}
          </span>
          {draft.length >= LIMIT_COUNTER_FROM && (
            <span
              className={cn('font-mono', atLimit && 'text-warning')}
              data-testid="assistant-question-limit"
            >
              {t('composer.limit', {
                count: draft.length,
                max: ASSISTANT_QUESTION_MAX_CHARS,
              })}
            </span>
          )}
        </p>
      </form>

      <ConfirmDialog
        isOpen={repeatCandidate !== null}
        onClose={() => setRepeatCandidate(null)}
        onConfirm={confirmRepeat}
        title={t('composer.repeatTitle')}
        message={t('composer.repeatDescription')}
        confirmText={t('composer.repeatConfirm')}
        cancelText={t('composer.repeatCancel')}
        variant="info"
      />
    </>
  );
}
